<template>
  <div class="saas-services-page">
    <!-- Section hero -->
    <!-- Header avec dégradé -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Nos solutions SaaS</h1>
        <p class="subtitle">Faites évoluer votre entreprise avec nos outils enrichis par l'IA</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Transformez votre business avec nos solutions SaaS</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                Découvrez nos solutions SaaS innovantes, conçues pour optimiser et faciliter la gestion de votre entreprise grâce à l'intelligence artificielle. Nos outils sont développés sur mesure pour répondre à vos besoins professionnels spécifiques.
              </p><br>
              <p>
                ✓ <strong><i>Des fonctionnalités personnalisables</i></strong> : Le SaaS s'intègre parfaitement à vos flux de travail et répond à vos besoins.
              </p><br>
              <p>
                ✓ <strong><i>Sécurité et conformité</i></strong> : Nous concevons l'infrastructure technique de manière à assurer la protection des données en accord avec la règlementation, à ce qu'elle soit suffisamment solide pour traiter de gros volumes et capable d'évoluer au même rythme que votre entreprise.
              </p><br>
              <p>
                ✓ <strong><i>Support dédié et accompagnement</i></strong> : Au delà du logiciel en lui-même, notre support sur-mesure inclut une phase de tests et de recette avant mise en production, des aides à la prise en main, une documentation complète et un support technique premium dédié.
              </p>
            </div>
            <div class="illustration-container">
              <iframe 
                style="width: 100%; height: 100%; border: none; border-radius: 10px;"
                src="https://www.youtube.com/embed/NGf2YgDIgiI" 
                title="Présentation de nos solutions SaaS"
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen>
              </iframe>
            </div>
          </div>
        </div>
      </section>

    <!-- Présentation des solutions SaaS -->
    <section class="solutions-section">
      <div class="container">
        <h2 class="section-title">Découvrez nos solutions SaaS</h2>
        <p class="section-subtitle">Des outils sur mesure et augmentés par l'IA pour répondre à vos besoins professionnels</p>
        
        <!-- Pulsar IA -->
        <div class="solution-card">
          <div class="solution-content">
            <h3 class="solution-title data-title">Pulsar IA</h3>
            <div class="solution-tagline">La première plateforme d'engagement client entièrement automatisée par l'IA</div>
            <div class="solution-description">
              <p>
                <strong>Pulsar IA</strong> est bien plus qu'un CRM : c'est une infrastructure IA complète qui met à votre disposition une équipe d'agents IA experts en marketing digital, relation commerciale et service client, travaillant pour vous 24h/24 et 7j/7.
              </p>
              <p>
                Notre solution transforme votre façon d'interagir avec vos prospects et clients. Avec Pulsar IA, vous gagnez jusqu'à 20 heures de travail par semaine en automatisant les tâches répétitives : prospection, collecte de leads, gestion des rendez-vous, community management, collecte d'avis Google, prise en charge de la clientèle et fidélisation.
              </p>
              <p>
                Pulsar IA s'adapte à tous les types d'entreprises (agences immobilières, cabinets de conseil, e-commerce, artisans) et intègre nativement tous les outils nécessaires : chatbot IA, call center, newsletters, campagnes marketing, prise de rendez-vous et base de contacts unifiée.
              </p>
            </div>
            <div class="solution-features">
              <h4>Des agents IA intégrés pour automatiser votre business</h4>
              <ul>
                <li>Chatbot IA expert connecté à votre base de connaissances</li>
                <li>Call center IA qui répond à 95% des appels automatiquement</li>
                <li>Campagnes marketing emails et SMS entièrement automatisées</li>
                <li>Newsletters personnalisées générées et envoyées par IA</li>
                <li>Community manager IA pour vos réseaux sociaux</li>
                <li>Prise de rendez-vous automatique avec synchronisation calendrier</li>
                <li>Base de contacts unifiée et segmentée</li>
              </ul>
            </div>
            <div class="solution-testimonials">
              <div class="testimonial">
                <div class="testimonial-content">
                  "Pulsar IA a amélioré notre façon de travailler. Nous avons augmenté la fréquence de nos posts et notre présence sur les réseaux sociaux de 200%."
                </div>
                <div class="testimonial-author">Marie D, Directrice Marketing</div>
              </div>
            </div>
            <div class="solution-cta">
              <ButtonCTA to="https://iaagence.fr/">En savoir plus</ButtonCTA>
            </div>
          </div>
        </div>
        
        <!-- Meeting Transcript -->
        <div class="solution-card">
          <div class="solution-content">
            <h3 class="solution-title meeting-title">Meeting Transcript</h3>
            <div class="solution-tagline">Transcription et analyse de réunions assistées par IA</div>
            <div class="solution-description">
              <p>
                <strong>Meeting Transcript</strong> tout en un qui optimise et facilite la gestion des réunions grâce à l'intelligence artificielle. Notre outil convertit automatiquement l'audio de vos réunions en texte avec une précision exceptionnelle, tout en reconnaissant les différents locuteurs.
              </p>
              <p>
                Au-delà de la simple transcription, Meeting Transcript génère automatiquement des résumés clairs et des synthèses pertinentes après chaque réunion, permettant à votre équipe de gagner un temps considérable et d'améliorer sa productivité.
              </p>
              <p>
                Notre solution s'adapte à de nombreux contextes professionnels : travail en équipe, recrutement, éducation, santé, commerce et journalisme. La plateforme est simple à utiliser et s'intègre facilement à vos outils existants, avec un chiffrement de bout en bout conforme au RGPD.
              </p>
            </div>
            <div class="solution-features">
              <h4>Des fonctionnalités optimisées par une équipe d'agents IA</h4>
              <ul>
                <li>Plateforme tout en un : audioconférence et transcription</li>
                <li>Enregistrement audio de conférences</li>
                <li>Transcription audio en texte par IA avec reconnaissance des locuteurs</li>
                <li>Génération automatisée de résumés téléchargeables</li>
                <li>Importation de fichiers audio externes pour transcription avec une précision &gt; 98%</li>
                <li>Traitement en moins d'une heure pour les réunions standards</li>
                <li>Support des formats audio courants (MP3, OGG, WAV, FLAC)</li>
              </ul>
            </div>
            <div class="solution-testimonials">
              <div class="testimonial">
                <div class="testimonial-content">
                  "Meeting Transcript a révolutionné notre façon de collaborer. Nous avons réduit le temps passé à prendre des notes de 70%."
                </div>
                <div class="testimonial-author">Marie D, Directrice Marketing</div>
              </div>
            </div>
            <div class="solution-cta">
              <ButtonCTA to="https://www.meetingtranscript.fr/">En savoir plus</ButtonCTA>
            </div>
          </div>
        </div>
        
        <!-- SEO Boost -->
        <div class="solution-card">
          <div class="solution-content">
            <h3 class="solution-title seo-title">SEO Boost</h3>
            <div class="solution-tagline">Optimisez votre référencement web avec les technologies IA</div>
            <div class="solution-description">
              <p>
                <strong>SEO Boost</strong> est un outil complet qui vous aide à booster votre visibilité sur les moteurs de recherche. Notre solution réalise un audit SEO détaillé de vos pages web pour identifier les problèmes techniques, les soucis d'indexation et les opportunités d'optimisation.
              </p>
              <p>
                Notre outil IA compare votre page web avec la concurrence et identifie les optimisations possibles sur les métriques SEO qui comptent vraiment. Vous obtenez une analyse complète incluant le top 10 de la concurrence sur Google, une comparaison sur les principales métriques SEO, et des graphiques simples et faciles à lire.
              </p>
              <p>
                SEO Boost réalise également une analyse sémantique approfondie, comparant le champ sémantique de votre page web avec la concurrence pour vous aider à découvrir les mots-clés stratégiques à utiliser dans votre contenu. Les données sont présentées en temps réel et l'interface est conviviale et intuitive.
              </p>
            </div>
            <div class="solution-features">
              <h4>Des fonctionnalités pour renforcer votre présence en ligne</h4>
              <ul>
                <li>Analyse approfondie du référencement de votre page web</li>
                <li>Identification des problèmes d'indexation et analyse de statut</li>
                <li>Comparaison avec le top 10 des concurrents sur Google</li>
                <li>Analyse sémantique et wordcloud des mots-clés</li>
                <li>Visualisation simple des métriques essentielles</li>
                <li>Suggestions d'optimisation pour votre contenu</li>
                <li>Rapports et données exportables au format Excel</li>
              </ul>
              </div>
              <div class="solution-testimonials">
              <div class="testimonial">
                  <div class="testimonial-content">
                  "SEO Boost est super facile à utiliser. J'ai pu voir qui étaient vraiment mes concurrents et créer de nouveaux contenus plus adaptés aux attentes de mes lecteurs."
                  </div>
                  <div class="testimonial-author">Jennifer, Influenceuse et blogueuse mode</div>
              </div>
              </div>
              <div class="solution-cta">
              <ButtonCTA to="https://www.graine-digitale.fr/boostez_votre_seo.html">En savoir plus</ButtonCTA>
              </div>
          </div>
          </div>
          
          <!-- Contact Finder Pro -->
          <div class="solution-card">
          <div class="solution-content">
              <h3 class="solution-title contact-title">Contact Finder Pro</h3>
              <div class="solution-tagline">Solution de prospection et gestion de contacts intelligente</div>
              <div class="solution-description">
              <p>
                <strong>Contact Finder Pro</strong> est notre solution avancée pour la prospection commerciale et la gestion de contacts professionnels. Cet outil vous permet d'identifier et de qualifier rapidement des prospects pertinents pour votre business grâce à l'intelligence artificielle.
              </p>
              <p>
                Notre solution vous fournit des contacts précis et à jour, avec les différents moyens de contacter les entreprises, leurs coordonnées email, téléphone et réseaux sociaux. Contact Finder Pro facilite ainsi l'enrichissement de données pour compléter vos bases existantes.
              </p>
              <p>
                L'outil vous permet de segmenter vos prospects selon leur présence en ligne ou sur les réseaux sociaux et d'automatiser vos campagnes de prospection. Il s'intègre parfaitement avec vos outils CRM existants et respecte scrupuleusement les normes RGPD pour une utilisation en toute conformité.
              </p>
              </div>
              <div class="solution-features">
              <h4>Des fonctionnalités pensées pour optimiser la recherche de contacts</h4>
              <ul>
                  <li>Identification de prospects qualifiés en temps réel</li>
                  <li>Enrichissement automatique de vos bases de données</li>
                  <li>Segmentation pour des campagnes ciblées</li>
                  <li>Intégration native avec vos outils CRM existants</li>
                  <li>Export de données aux formats standards</li>
                  <li>Conformité RGPD garantie</li>
              </ul>
              </div>
              <div class="solution-testimonials">
              <div class="testimonial">
                  <div class="testimonial-content">
                  "Grâce à Contact Finder Pro, nous avons augmenté notre taux de conversion de 45% en ciblant précisément les bons décideurs dans notre secteur."
                  </div>
                  <div class="testimonial-author">Thomas M, Directeur Commercial</div>
              </div>
              </div>
              <div class="solution-cta">
              <ButtonCTA to="https://www.graine-digitale.fr/contact_finder_facile.html">En savoir plus</ButtonCTA>
              </div>
          </div>
          </div>
      </div>
      </section>
      
      
      <!-- Section FAQ -->
      <section class="faq-section">
      <div class="container">
          <h2 class="section-title">Questions fréquentes</h2>
          
          <div class="faq-list">
          <div class="faq-item">
            <div class="faq-question">
            J'ai besoin de développer un SaaS, comment ça se passe ?
            </div>
            <div class="faq-answer">
            C'est très simple ! Nous définissons ensemble le cahier des charges et la roadmap. Dans un deuxième temps, nous développons les différentes fonctionnalités. Ensuite, vos équipes testent le produit en développement et nous ajustons en fonction de vos retours. Enfin, nous mettons en production le SaaS. Par la suite, vous bénéficiez de quelques semaines supplémentaires pour régler les derniers détails. 
            </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              Vos solutions SaaS sont-elles conformes au RGPD ?
              </div>
              <div class="faq-answer">
              Oui, toutes nos solutions sont entièrement conformes au Règlement Général sur la Protection des Données (RGPD). Nous utilisons un chiffrement de bout en bout pour sécuriser vos données et nous ne conservons aucune information non nécessaire au fonctionnement de nos services.
              </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              Comment fonctionne l'offre "à vie" ?
              </div>
              <div class="faq-answer">
              Notre offre à vie vous donne un accès permanent à nos solutions sans aucun paiement mensuel ou annuel supplémentaire. Elle est valable pour toute la durée d'existence de nos services, avec une garantie minimale de 3 ans. Vous bénéficiez également de toutes les mises à jour futures des solutions concernées.
              </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              Puis-je essayer vos solutions avant de m'engager ?
              </div>
              <div class="faq-answer">
              Absolument ! Pour SEO Boost et Contact Finder Pro, nous proposons une offre gratuite permettant de tester nos solutions avec un nombre limité de requêtes mensuelles. Cette formule vous permet d'évaluer nos outils avant de passer à un plan payant. Aucune carte bancaire n'est requise pour l'inscription.
              </div>
          </div>
          
          <div class="faq-item">
              <div class="faq-question">
              Quel support est inclus avec vos solutions ?
              </div>
              <div class="faq-answer">
              Les plans payants incluent un support technique par email avec une réponse garantie sous 24 heures. Par ailleurs, l'offre Bundle bénéficie d'un support prioritaire. Si vous le souhaitez, nous pouvons même organiser une session de formation personnalisée pour vous aider à tirer le meilleur parti de nos solutions.
              </div>
          </div>
          </div>
      </div>
      </section>
      
      <!-- Call to action final -->
      <section class="cta-section">
      <div class="container">
          <h2 class="cta-title">Prêt à transformer votre business ?</h2>
          <p class="cta-subtitle">Rejoignez des milliers d'entreprises qui utilisent nos solutions SaaS</p>
          <div class="cta-buttons">
            <span class="btn-secondary fake-link" @click="navigateTo('e1')">Prenez un rendez-vous en ligne / téléphonique / vidéo</span>
          </div>
      </div>
      </section>
    </div>
    </div>
  </template>

  <script>
  import ButtonCTA from '@/components/ui/ButtonCTA.vue'
  import { useHead } from '@vueuse/head'

  export default {
    name: 'SaasServicesPage',
    components: {
      ButtonCTA
    },
    setup() {
      useHead({
        title: 'Création de SaaS - Développement d\'applications Web | Arynae',
        meta: [
          { name: 'description', content: 'Développement sur mesure de logiciels SaaS (Software as a Service). Créez votre application web avec notre expertise technique et un design centré sur l\'utilisateur.' },
          { name: 'keywords', content: 'création SaaS, développement logiciel, application web, software as a service, développement sur mesure, MVP, interface utilisateur, UX, UI, Arynae' },
          { name: 'robots', content: 'index, follow' },
          { name: 'author', content: 'Arynae' },
          { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
          // Open Graph essentiels
          { property: 'og:type', content: 'website' },
          { property: 'og:title', content: 'Création de SaaS - Développement d\'applications Web | Arynae' },
          { property: 'og:description', content: 'Développement sur mesure de logiciels SaaS (Software as a Service). Créez votre application web avec notre expertise technique et un design centré sur l\'utilisateur.' },
          { property: 'og:url', content: 'https://arynae.com/saas' },
          { property: 'og:locale', content: 'fr_FR' },
          { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
          { property: 'og:image:alt', content:'Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
          // Twitter Card basiques
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: 'Création de SaaS - Développement d\'applications Web | Arynae' },
          { name: 'twitter:description', content: 'Développement sur mesure de logiciels SaaS (Software as a Service). Créez votre application web avec notre expertise technique et un design centré sur l\'utilisateur.' },
          { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
        ],
        link: [
          { rel: 'canonical', href: 'https://arynae.com/saas' },
          { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/saas' }
        ],
        htmlAttrs: {
          lang: 'fr'
        }
      })
      
      return {}
    },
    data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
  </script>

  <style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';

.saas-services-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem;
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block;
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Intro Card with Illustration */
.text-and-illustration {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.text-and-illustration .text-content {
  flex: 1;
  min-width: 300px;
}

/* Juste après ces règles CSS existantes */
.illustration-container {
  flex: 0.7;
  min-width: 200px;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Ajoutez uniquement cette media query */
@media (max-width: 768px) {
  .text-and-illustration {
    flex-direction: column;
  }
  
  .text-and-illustration .text-content,
  .illustration-container {
    min-width: 0;
    width: 100%;
  }
}

  // Section Solutions
  .solutions-section {
  padding: $spacing-xl 0;
  
  .section-title {
      font-size: 36px;
      font-weight: bold;
      color: $dark-text;
      margin-bottom: $spacing-sm;
      text-align: center;
  }
  
  .section-subtitle {
      font-size: 18px;
      color: $medium-text;
      margin-bottom: $spacing-xl;
      text-align: center;
  }
  
  .solution-card {
      background-color: $white;
      border-radius: $border-radius-xl;
      padding: $spacing-xl;
      margin-bottom: $spacing-xl;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
      
      .solution-content {
      width: 100%;
      }
  }
  
  .solution-title {
      font-size: 30px;
      font-weight: bold;
      margin-bottom: $spacing-xs;
      
      &.meeting-title {
      color: #7400b8;
      }
      
      &.seo-title {
      color: #5390d9;
      }
      
      &.contact-title {
      color: #ff006e;
      }
      
      &.data-title {
      color: rgb(59, 130, 246);
      }
  }
  
  .solution-tagline {
      font-size: 18px;
      color: $medium-text;
      margin-bottom: $spacing-md;
  }
  
  .solution-description {
      margin-bottom: $spacing-md;
      
      p {
      margin-bottom: $spacing-sm;
      line-height: 1.6;
      }
  }
  
  .solution-features {
      margin-bottom: $spacing-md;
      
      h4 {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: $spacing-sm;
      }
      
      ul {
      list-style-type: disc;
      padding-left: 20px;
      
      li {
          margin-bottom: $spacing-xs;
      }
      }
  }
  
  .solution-testimonials {
      background-color: $light-gray;
      padding: $spacing-md;
      border-radius: $border-radius-md;
      margin-bottom: $spacing-md;
      
      .testimonial-content {
      font-style: italic;
      margin-bottom: $spacing-xs;
      }
      
      .testimonial-author {
      font-weight: bold;
      }
  }
  
  .solution-cta {
      margin-top: $spacing-md;
  }
  }

  // Section FAQ
  .faq-section {
  padding: $spacing-xl 0;
  
  .section-title {
      font-size: 36px;
      font-weight: bold;
      color: $dark-text;
      margin-bottom: $spacing-xl;
      text-align: center;
  }
  
  .faq-list {
      max-width: 800px;
      margin: 0 auto;
  }
  
  .faq-item {
      margin-bottom: $spacing-md;
      border-bottom: 1px solid $light-gray;
      padding-bottom: $spacing-md;
      
      &:last-child {
      border-bottom: none;
      }
  }
  
  .faq-question {
      font-size: 18px;
      font-weight: bold;
      color: $dark-text;
      margin-bottom: $spacing-sm;
      cursor: pointer;
  }
  
  .faq-answer {
      color: $medium-text;
      line-height: 1.6;
  }
  }

  // Section CTA
  .cta-section {
  padding: $spacing-xl 0;
  background: $accent-gradient;
  color: $white;
  text-align: center;
  
  .cta-title {
      font-size: 36px;
      font-weight: bold;
      margin-bottom: $spacing-sm;
  }
  
  .cta-subtitle {
      font-size: 18px;
      margin-bottom: $spacing-lg;
      opacity: 0.9;
  }
  
  .cta-buttons {
      display: flex;
      justify-content: center;
      gap: $spacing-md;
      
      @media (max-width: $breakpoint-sm) {
      flex-direction: column;
      align-items: center;
      }
  }
  }

  // Styles des boutons
  .btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: bold;
  color: $white;
  background-color: transparent;
  border: 2px solid $white;
  border-radius: $border-radius-pill;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      transform: translateY(-2px);
  }
  
  .cta-section & {
      border-color: $white;
      color: $white;
  }
  
  .hero-section & {
      border-color: #5e60ce;
      color: #5e60ce;
  }
  }

  /* Ajoutez après les règles de .solutions-section */
@media (max-width: 768px) {
  .solutions-section .container {
    padding: 0;
  }
  
  .solution-card {
    padding: 1.5rem;
  }
}
  </style>